import {
  ContactInfo,
  EnumProductTypes,
  Listing,
  OwnerContactInfo,
  Address,
  Note,
  Socials,
} from "../types/listing";
import { convertApiGeoLocationToFrontend } from "./convertApiGeoLocationToFrontend";
import {
  Lead,
  Note as ServerNote,
  Property,
  SocialMedia,
} from "../types/neighborhoodSearch";
import { PropertyTypeEnum } from "src/data/selectionData";
import { formatNumbersInString } from "./numbers";
import { formatDate } from "./dates";
import formatPhoneNumber from "src/components/common/formatPhones";
import capitalizeWords from "src/components/common/capitalizeWords";


/**
 * Converts SocialMedia array to Socials object
 * @param socialMediaArray - Array of SocialMedia objects from API
 * @returns A Socials object with platform-specific URLs
 */
export const parseSocialMediaToSocials = (
  socialMediaArray: SocialMedia[] | null | undefined
): Socials | undefined => {
  if (!socialMediaArray || !socialMediaArray.length) {
    return undefined;
  }

  const socials: Socials = {};

  socialMediaArray.forEach((social) => {
    const network = social.network?.toLowerCase();
    const username = social.username;

    if (!network || !username) {
      return; // Skip invalid entries
    }

    // Create URL based on platform
    switch (network) {
      case 'facebook':
        socials.facebook = `https://facebook.com/${username}`;
        break;
      case 'twitter':
        socials.twitter = `https://x.com/${username}`;
        break;
      case 'instagram':
        socials.instagram = `https://instagram.com/${username}`;
        break;
      case 'linkedin':
        socials.linkedin = `https://linkedin.com/in/${username}`;
        break;
      default:
        // Skip unknown platforms
        break;
    }
  });

  // Return undefined if no valid social media was found
  return Object.keys(socials).length > 0 ? socials : undefined;
};

/**
 * Formats an Address object into a human-readable string.
 * @param address - The Address object to format
 * @param includeZipPlus4 - Whether to include the +4 portion of the zip code (default: false)
 * @returns A formatted address string (e.g., "123 Main St, New York, NY 10001")
 */
export const formatAddress = (
  address: Address | null | undefined,
  includeZipPlus4 = false
): string => {
  if (!address) {
    return '';
  }

  // If granular fields are available, build from them
  if (address.houseNumber || address.streetName) {
    const streetParts = [
      address.houseNumber,
      address.streetPreDirection,
      address.streetName,
      address.streetSuffix,
      address.streetPostDirection,
    ].filter(Boolean).join(' ');

    const addressParts = [streetParts, address.city, address.state].filter(Boolean);
    
    // Add zip code
    if (address.postalCode) {
      let zipCode = address.postalCode;
      if (includeZipPlus4 && address.postalCodePlus4) {
        zipCode += `-${address.postalCodePlus4}`;
      }
      addressParts.push(zipCode);
    }

    return addressParts.join(', ');
  }

  // Fall back to using the formattedAddress or construct from basic fields
  if (address.formattedAddress) {
    return address.formattedAddress;
  }

  // Last resort: construct from basic fields
  const basicParts = [address.streetAddress, address.city, address.state, address.postalCode].filter(Boolean);
  return basicParts.join(', ');
};

/**
 * Formats an address with fallback handling for missing components.
 * @param address - The Address object to format
 * @param options - Formatting options
 * @returns A formatted address string with appropriate fallbacks
 */
export const formatAddressWithFallbacks = (
  address: Address | null | undefined,
  options: {
    includeZipPlus4?: boolean;
    fallbackText?: string;
    showDashes?: boolean;
  } = {}
): string => {
  const { includeZipPlus4 = false, fallbackText = "Address not available", showDashes = false } = options;
  
  if (!address) {
    return fallbackText;
  }

  // Try standard formatting first
  const formatted = formatAddress(address, includeZipPlus4);
  if (formatted) {
    return formatted;
  }

  // If no formatted address, try with fallback components
  if (showDashes) {
    const streetPart = address.streetAddress || "--";
    const cityPart = address.city || "--";
    const statePart = address.state || "--";
    const zipPart = address.postalCode || "--";
    return `${streetPart}, ${cityPart}, ${statePart} ${zipPart}`;
  }

  return fallbackText;
};

/**
 * Formats an address for URL encoding (Google Maps, etc.)
 * @param address - The Address object to format
 * @returns A URL-encoded address string (e.g., "123+Main+St+New+York+NY+10001")
 */
export const formatAddressForUrl = (
  address: Address | null | undefined
): string => {
  const formatted = formatAddress(address);
  if (!formatted) {
    return '';
  }
  
  // Replace spaces and commas with + for URL encoding
  return formatted.replace(/[, ]+/g, '+');
};


/**
 * Creates an Address object from individual components
 * @param components - Address components
 * @returns An Address object
 */
export const createAddressFromComponents = (components: {
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  houseNumber?: string | null;
  unit?: string | null;
  streetName?: string | null;
  streetSuffix?: string | null;
  streetPreDirection?: string | null;
  streetPostDirection?: string | null;
  postalCodePlus4?: string | null;
}): Address => {
  // Build formatted address
  const streetWithUnit = [components.streetAddress, components.unit].filter(Boolean).join(' ');
  const addressParts = [
    streetWithUnit,
    components.city,
    components.state,
    components.postalCode
  ].filter(Boolean);
  
  const formattedAddress = addressParts.join(', ');

  return {
    streetAddress: components.streetAddress || '',
    city: components.city || '',
    state: components.state || '',
    postalCode: components.postalCode || '',
    formattedAddress,
    // Include granular fields if provided
    houseNumber: components.houseNumber || undefined,
    streetName: components.streetName || undefined,
    streetSuffix: components.streetSuffix || undefined,
    streetPreDirection: components.streetPreDirection || undefined,
    streetPostDirection: components.streetPostDirection || undefined,
    postalCodePlus4: components.postalCodePlus4 || undefined,
    unit: components.unit || undefined,
  };
};


/**
 * Converts the isShowOnlyPrivatelyOwned boolean to companyOwned parameter.
 * Returns undefined if input is null or undefined to avoid filtering.
 * Otherwise returns the inverted boolean value.
 */
export const convertPrivatelyOwnedToCompanyOwned = (
  isShowOnlyPrivatelyOwned: boolean | null | undefined
): boolean | undefined => {
  if (isShowOnlyPrivatelyOwned === null || isShowOnlyPrivatelyOwned === undefined) {
    return undefined;
  }
  return !isShowOnlyPrivatelyOwned;
};

export const getAddressFromPropertyInfo = (propertyInfo: Property): Address => {
  return createAddressFromComponents({
    streetAddress: [
      propertyInfo.house_number,
      propertyInfo.street_predirection,
      propertyInfo.street_name,
      propertyInfo.street_suffix,
      propertyInfo.street_postdirection,
    ].filter(Boolean).join(' '),
    city: propertyInfo.city,
    state: propertyInfo.state,
    postalCode: propertyInfo.postal_code,
    // Include granular fields
    houseNumber: propertyInfo.house_number,
    streetName: propertyInfo.street_name,
    streetSuffix: propertyInfo.street_suffix,
    streetPreDirection: propertyInfo.street_predirection,
    streetPostDirection: propertyInfo.street_postdirection,
    postalCodePlus4: propertyInfo.postal_code_plus4,
    unit: propertyInfo.unit,
  });
};

export const getContactsFromLead = (lead: Lead): ContactInfo[] => {
  return (
    (lead.contact_info?.contacts || []).map(
      (contact, index) =>
        ({
          id: index.toString(),
          fullName: capitalizeWords(`${contact.first_name || ""} ${
            contact.last_name || ""
          }`.trim()) || "",
          firstName: capitalizeWords(contact.first_name) || "",
          lastName: capitalizeWords(contact.last_name) || "",
          phone: contact.phone_number || "",
          formattedPhone: formatPhoneNumber(contact.phone_number),
          dnc: contact.do_not_call || false,
          phoneType: contact.phone_type || "",
          isFavorite: false,
          hidden: false,
          listingId: lead.id,
        } as ContactInfo)
    ) || []
  );
};

export const getOwnerContactInfoFromLead = (
  lead: Lead
): OwnerContactInfo => {
  const propertyInfo = lead.property_info;
  const primaryOwnerInfo = propertyInfo.owner_info?.party_owners?.[0]
  const firstContact = lead.contact_info?.contacts?.[0];
  const socials = firstContact?.social_media;
  const mailingAddress = propertyInfo.owner_info?.mailing_address
  const {
    house_number: houseNumber = "",
    street_predirection: streetPreDirection = "",
    street_name: streetName = "",
    street_suffix: streetSuffix = "",
    street_postdirection: streetPostDirection = "",
    city = "",
    state = "",
    postal_code: postalCode = "",
    postal_code_plus4: postalCodePlus4 = "",
    unit = "",
  } = mailingAddress as Property || {};

  // Use the utility function to create proper address
  const streetParts = [
    houseNumber,
    streetPreDirection,
    streetName,
    streetSuffix,
    streetPostDirection,
  ].filter(Boolean);
  const streetAddress = streetParts.join(' ');

  const address = createAddressFromComponents({
    streetAddress,
    city,
    state,
    postalCode,
    houseNumber,
    streetName,
    streetSuffix,
    streetPreDirection,
    streetPostDirection,
    postalCodePlus4,
    unit,
  });

  return {
    fullName: capitalizeWords(primaryOwnerInfo?.full_name) || "N/A",
    firstName: capitalizeWords(primaryOwnerInfo?.first_name) || "",
    lastName: capitalizeWords(primaryOwnerInfo?.last_name) || "",
    email: firstContact?.email,
    socials: parseSocialMediaToSocials(socials),
    address,
  };
};

export const getListingFromLead = (lead: Lead): Listing => {
  const listingAddress = getAddressFromPropertyInfo(lead.property_info);
  const contacts = getContactsFromLead(lead);

  // Extract demographics from the first contact if available
  const firstContact = lead.contact_info?.contacts?.[0];
  const demographics = firstContact ? {
    age: firstContact.age,
    income: firstContact.income,
    occupation: firstContact.occupation,
    education: firstContact.education,
    gender: firstContact.gender,
    maritalStatus: firstContact.marital_status,
    numberOfChildren: firstContact.number_of_children,
    homeValueRange: firstContact.home_value_range ? {
      start: firstContact.home_value_range.start,
      end: firstContact.home_value_range.end,
    } : undefined,
    netWorth: firstContact.net_worth,
    businessOwner: firstContact.business_owner,
  } : undefined;
  
  return {
    id: lead.id,
    propertyId: lead.property_info?.id,
    ownerOccupied: lead.property_info?.owner_info?.owner_occupied,
    publishDate: lead.create_timestamp,
    formattedPublishDate: formatDate(lead.create_timestamp),
    productType: EnumProductTypes.ns,
    geolocation: lead.property_info?.geolocation
      ? convertApiGeoLocationToFrontend(lead.property_info.geolocation)
      : undefined,
    address: listingAddress,
    isFavorite: lead.favorite,
    title: listingAddress.formattedAddress,
    mapLink: (() => {
      const urlAddress = formatAddressForUrl(listingAddress);
      return urlAddress
        ? `https://www.google.com/maps/place/${urlAddress}`
        : "";
    })(),
    beds: Number(lead.property_info?.details?.bedrooms) || 0,
    bath: Number(lead.property_info?.details?.bathrooms) || 0,
    sqrFeet: Number(lead.property_info?.details?.square_footage) || 0,
    formattedSqrFeet: formatNumbersInString(
      String(lead.property_info?.details?.square_footage || "0")
    ),
    lotSize: String(lead.property_info?.details?.acreage),
    formattedLotSize: formatNumbersInString(
      String(lead.property_info?.details?.acreage || "0")
    ),
    yearBuilt: String(lead.property_info?.details?.year_built || ""),
    typeCode: lead.property_info?.details?.property_type || "",
    type:
      PropertyTypeEnum?.[
        lead.property_info?.details
          ?.property_type as keyof typeof PropertyTypeEnum
      ] || "",

    notes:
      (lead.notes || []).map(
        ({
          id,
          note,
          modify_timestamp: updatedAt,
          create_timestamp: createdAt,
        }: ServerNote): Note =>
          ({
            id,
            note,
            updatedAt,
            createdAt,
            formattedUpdatedAt: formatDate(updatedAt),
            formattedCreatedAt: formatDate(createdAt),
          } as Note)
      ) || [],
    tags: lead.tags || [],
    ownerContactInfo: getOwnerContactInfoFromLead(lead),
    contacts: contacts,
    demographics: demographics,
  } as Listing;
};

/**
 * Get the maximum year for year built filters (current year)
 */
export const getYearBuiltMax = (): number => {
  return new Date().getFullYear();
};

/**
 * CSV Export functionality for neighborhood search listings
 */

/**
 * Escape CSV field value to handle quotes, commas, and newlines
 * Always quotes string values for maximum safety and consistency
 */
const escapeCSVField = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '""'; // Quote empty values for consistency
  }
  
  const stringValue = String(value);
  
  // Always escape quotes by doubling them and wrap in quotes for safety
  const escapedValue = stringValue.replace(/"/g, '""');
  return `"${escapedValue}"`;
};

/**
 * Generate CSV content from data array
 */
const generateCSVContent = (data: Record<string, unknown>[], headers: string[]): string => {
  let csvContent = '\uFEFF'; // UTF-8 BOM for Excel compatibility
  
  // Add headers
  csvContent += headers.map(header => escapeCSVField(header)).join(',') + '\n';
  
  // Add data rows
  data.forEach(row => {
    const values = Object.values(row);
    const csvRow = values.map(value => escapeCSVField(value)).join(',');
    csvContent += csvRow + '\n';
  });
  
  return csvContent;
};

/**
 * Generate timestamped filename for CSV export
 */
const generateCSVFileName = (baseName: string): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const currentDate = `${year}${month}${day}`;
  
  return `leads_export_${baseName}_${currentDate}.csv`;
};

/**
 * Download CSV file to user's computer
 */
const downloadCSVFile = (csvContent: string, filename: string): void => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  
  URL.revokeObjectURL(url);
};

/**
 * Export neighborhood search listings to CSV
 */
export const exportNeighborhoodSearchToCSV = (
  title: string,
  data: Record<string, unknown>[],
  headerMapping: Record<string, string>
): void => {
  if (!data || !data.length) {
    console.error('No data provided for CSV export');
    return;
  }

  const keys = Object.keys(headerMapping);
  const headers = Object.values(headerMapping);
  
  // Filter data to only include specified keys
  const filteredData = data.map(item => {
    const result: Record<string, unknown> = {};
    keys.forEach(key => {
      if (item.hasOwnProperty(key)) {
        result[key] = item[key];
      }
    });
    return result;
  });
  
  const csvContent = generateCSVContent(filteredData, headers);
  const filename = generateCSVFileName(title);
  
  downloadCSVFile(csvContent, filename);
};